import { compact, decimal, decimalUpTo, formatterFor, locale, parseLocaleNumber, separators, unformat } from 'Helpers/currencyFormat';

/*
 * Every formatter below formats through Helpers/currencyFormat, so grouping,
 * decimal separators and abbreviation scales follow the currency in scope. The
 * exported signatures are deliberately unchanged — the several hundred call
 * sites keep working as-is, and a currency mapped to an Anglo locale (which is
 * every currency the application supported before) produces byte-identical
 * output.
 */

// Intl renders a negative that rounds to zero as "-0". A figure with no non-zero
// digit drops the sign instead (currencyFormat pins Latin digits).
const unsignZero = (text) => (typeof text === 'string' && !/[1-9]/.test(text) ? text.replace(/^[-\u2212]/, '') : text);

export const formatSeparateByThousands = (val) => {
    return unsignZero(decimal(val));
};

export const formatMoney = (val) => {
    return formatSeparateByThousands(val);
};

// Rounds half away from zero, like PHP number_format() and decimal(v, 0), so -0.5
// is "-1": Math.round alone gives -0 there, and -1234.5 → -1234.
export const formatAndRoundMoney = (val) => {
    const n = Number(val);

    return formatSeparateByThousands(Math.sign(n) * Math.round(Math.abs(n)));
};

/**
 * DataTables `render` for a money column whose payload value is a raw numeric.
 *
 * Formats for DISPLAY only and returns the value untouched for every other
 * request type. That matters: DataTables calls render again for 'sort', 'filter'
 * and 'type', and handing it a grouped string there makes it sort money
 * lexically — "900" after "1,000" — and breaks numeric search. Formatting for
 * all types is the classic way this goes wrong.
 *
 * Uses decimal(…, 0) rather than formatAndRoundMoney: these cells replace a
 * value the server used to format with number_format($v, 0), which rounds halves
 * AWAY FROM ZERO. Math.round does not — it rounds -1234.5 to -1234 where PHP
 * gives -1,235 — so rounding through the engine is what keeps the rendered text
 * identical to what the endpoint used to send.
 *
 * Non-numeric input is returned untouched, and that is load-bearing rather than
 * defensive. DataTables applies this render inside fnGetData BEFORE
 * _fnGetCellData tests `cellData === undefined` to substitute `defaultContent`
 * (dataTables.js:2145 then :2749). So returning the string "NaN" for a missing
 * key makes that test false and suppresses `defaultContent: 0` — a sponsor with
 * 3 of 24 populated months would show 21 cells reading "NaN". Passing undefined
 * through keeps the substitution working.
 *
 * Signature is DataTables' own, so it can be passed straight as `render`.
 */
export const dataTableMoneyRender = (data, type) => {
    if (data === null || data === undefined || data === '') {
        return data;
    }

    const numeric = Number(data);

    if (!Number.isFinite(numeric)) {
        return data;
    }

    if (type === 'display') {
        return unsignZero(decimal(numeric, 0));
    }

    // Search matches against BOTH conventions (AC: "numeric matching must work
    // whichever separator convention the user types"). Before the payload went
    // raw, DataTables filtered the server's grouped string, so a user typing
    // "17,684" matched; filtering the bare number alone would silently drop
    // that. Returning both keeps the grouped form working and adds the raw one.
    // 'sort' and 'type' still get the number, so ordering stays numeric.
    if (type === 'filter') {
        return `${unsignZero(decimal(numeric, 0))} ${numeric}`;
    }

    return data;
};

/**
 * Format a payload value that is written straight into the DOM — footer cells,
 * subtotal cells, group totals — rather than through a DataTables `render`.
 *
 * Those rows mix money with labels ("Overall Total") and deliberate blanks, so
 * anything non-numeric is passed through untouched. Rounds like the endpoint's
 * previous number_format($v, 0), so the rendered text is unchanged for Anglo
 * currencies.
 *
 * These write paths are the ones a per-column check cannot see: a view can have
 * a formatted render on every column and still interpolate a raw footer value.
 */
export const formatPayloadMoney = (value) => {
    if (value === null || value === undefined || value === '') {
        return value;
    }

    const numeric = Number(value);

    return Number.isFinite(numeric) ? unsignZero(decimal(numeric, 0)) : value;
};

// formatPayloadMoney under the table-figure rule: an exact zero as ZERO_DASH, a
// value that rounds to zero as "<1", nothing entered as blank.
export const formatPayloadFigure = (value) => formatFigure(value, formatPayloadMoney);

// dataTableMoneyRender with the figure rule on 'display'; every other type is
// untouched. A key the row omits shows the column's defaultContent through the
// same rule (0 → ZERO_DASH, '' → blank); with no defaultContent it stays
// undefined so DataTables' own substitution applies. `meta` is absent when a
// caller invokes the render directly.
export const dataTableFigureRender = (data, type, row, meta) => {
    if (type !== 'display') {
        return dataTableMoneyRender(data, type);
    }

    if (data === undefined) {
        // sDefaultContent is DataTables' internal (Hungarian) name for a column's `defaultContent`.
        const fallback = meta?.settings?.aoColumns?.[meta?.col]?.sDefaultContent;

        return fallback === undefined || fallback === null ? undefined : formatPayloadFigure(fallback);
    }

    return formatPayloadFigure(data);
};

// DataTables render for a client-side money column shown at up to three decimals:
// the figure rule on 'display' (a key the row omits is a zero), the grouped figure
// plus the raw number for 'filter' so "1,234" still finds the row, and the raw
// value for 'sort' / 'type'.
export const moneyFigureRender = (data, type) => {
    if (type === 'display') {
        return formatFigure(data === undefined ? 0 : data, formatMoney, { precision: 3 });
    }

    if (type === 'filter' && data !== null && data !== undefined && data !== '') {
        return `${formatMoney(data)} ${data}`;
    }

    return data;
};

// Strips formatting back to a plain, locale-independent numeric string, so the
// value can be re-formatted for display. Reads the separators of the active
// locale rather than assuming commas group and dots divide — under a
// comma-decimal currency the old blanket comma-strip turned "14.250.000,50"
// into "14.250.00050", i.e. 14.25. Unparseable input is passed through
// untouched, as it was before.
export const formatToLocaleString = (val) => {
    const normalised = unformat(val);

    return normalised === null ? String(val) : normalised;
};

export const formatMoneyWithLocale = (val) => {
    return formatMoney(formatToLocaleString(val));
};

// A negative that rounds to zero renders "0", never "(0)".
export const formatNegativeMoney = (val) => {
    return bracketNegative(val, formatAndRoundMoney(Math.abs(val)));
};

// A plain, locale-independent numeric string at a fixed precision — the
// intermediate that formatToFixedMoneyWithLocale then formats for display.
export const formatToFixedLocaleString = (val, precision = 0) => {
    return Number(val).toFixed(precision);
};

// A negative that rounds to zero renders "0", never "(0)".
export const formatToFixedMoneyWithLocale = (val, precision = 0) => {
    return val >= 0
        ? formatMoney(formatToFixedLocaleString(val, precision))
        : bracketNegative(val, formatMoney(formatToFixedLocaleString(Math.abs(val), precision)));
};

// Compact money for headline figures (e.g. dashboards): 35_800_000 -> "35.8M",
// 696_000 -> "696k". Trailing ".0" is dropped ("8M", not "8.0M"). Values under 1,000
// fall back to the thousands-separated integer. Sign is preserved.
//
// The scales and suffixes now come from the currency's config entry, so INR
// abbreviates in lakh/crore (12.5L, 4.5Cr) and TRY in billions at two decimals
// (14,25b). Anything without an entry keeps the M/k convention above exactly.
export const formatCompactMoney = (val) => {
    return compact(val);
};

// Wrap an already-formatted MAGNITUDE in parentheses when the value it came from is
// negative. The magnitude is formatted from the absolute value, so Intl never sees a
// negative and cannot emit the "-0" that zero fraction digits return just below zero. A
// magnitude that itself rounded down to the literal string "0" (e.g. the title of a
// -0.0001 deficit at 3 decimals) is left unbracketed too — "(0)" reads as a signed
// zero, which is exactly what this helper exists to prevent.
const bracketNegative = (value, magnitude) => (value < 0 && magnitude !== '0' ? `(${magnitude})` : magnitude);

// What every table shows in place of a value that is exactly zero (an en dash).
export const ZERO_DASH = '–';

const isBlankFigure = (value) => value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

// Zero, or the float noise a client-side sum leaves behind (0.1 + 0.2 - 0.3).
const isZeroFigure = (n) => Math.abs(n) < 1e-9;

// A non-zero value the formatter would round to zero at `precision` decimals. The
// threshold is half the smallest step the precision can show, so 0.5 still rounds
// to 1 at 0dp rather than flooring.
const roundsToZero = (value, precision) => value !== 0 && Math.abs(value) < 0.5 / 10 ** precision;

// The Overview's floor magnitude: the smallest step the precision can show, in the
// active locale — "<1", "<0.1", "<0,1".
const floorFigure = (precision) => `<${decimal(1 / 10 ** precision, precision)}`;

// Display text for a table figure, wrapped around the table's own formatter:
//
//   null / undefined / ''          → ''          nothing entered: blank, never a dash
//   non-numeric text               → unchanged   already-formatted text passes through
//   zero (0, -0, "0.00", noise)    → ZERO_DASH
//   non-zero, rounds to zero       → "<1", "<0.01", "<0.1%"; negative "(<1)", "(<0.1)%"
//   anything else                  → format(value, precision) + suffix
//
// `format` must take (value, precision) and keeps its own precision and negative
// convention. The suffix sits outside the parentheses on the floor, where the
// formatter's own negatives put it too. In a DataTables render, call this for
// 'display' only and hand sort / filter / type the raw number.
export const formatFigure = (value, format = formatToFixedMoneyWithLocale, { precision = 0, suffix = '' } = {}) => {
    if (isBlankFigure(value)) {
        return '';
    }

    const n = Number(value);

    if (Number.isNaN(n)) {
        return typeof value === 'string' ? value : '';
    }

    if (!isFinite(n)) {
        return '';
    }

    if (isZeroFigure(n)) {
        return ZERO_DASH;
    }

    // The floor's step goes through the same formatter, so it shares the column's
    // separators: "<0.01%" beside a toFixed "12.50%", "<0,01" beside a locale figure.
    if (roundsToZero(n, precision)) {
        return `${bracketNegative(n, `<${format(1 / 10 ** precision, precision)}`)}${suffix}`;
    }

    return `${format(value, precision)}${suffix}`;
};

// Budget Overview display formatter (event Financial Overview tab). Mirrors the
// approved redesign prototype's fmt() exactly: the currency's locale (en-AU and
// en-US group and divide identically, so Anglo output is unchanged), tabular
// figures, and unit-aware (millions / thousands / full). Nothing entered (null /
// undefined / '' / non-numeric) renders blank; a TRUE zero renders as ZERO_DASH; a
// value that merely ROUNDS to zero in the selected unit shows "<0.1m" / "<1k", so a
// dash only ever means genuine zero. Negatives keep a LEADING MINUS in every mode
// (a deliberate screen-local deviation from the app-wide parenthesis convention, for
// consistency across the toggle) — including the round-to-zero floor: a small
// deficit shows "-<0.1m" / "-<1k", not the sign-less floor string, so it can never
// be mistaken for a small surplus (the hover tooltip and the Net/AFC card's sign
// treatment also stay correct, but the cell text itself must not drop the sign).
// Returns { text, title, isZero }; `title` is always the full unabbreviated value
// for the hover tooltip. Not to be confused with formatCompactMoney (uppercase M,
// no round-to-zero floor, config-driven scales).
export const formatOverviewFigure = (value, unit = 'm') => {
    const n = isBlankFigure(value) ? NaN : Number(value);

    if (!isFinite(n)) {
        return { text: '', title: '', isZero: false };
    }

    if (isZeroFigure(n)) {
        return { text: ZERO_DASH, title: '0', isZero: true };
    }

    const title = decimal(n);

    if (unit === 'full') {
        const abs = Math.abs(n);
        return { text: bracketNegative(n, roundsToZero(abs, 0) ? floorFigure(0) : decimal(abs, 0)), title, isZero: false };
    }

    if (unit === 'k') {
        const k = n / 1e3;
        const text = Math.abs(k) >= 0.5 ? decimal(Math.round(k)) + 'k' : `${k < 0 ? '-' : ''}<1k`;
        return { text, title, isZero: false };
    }

    const m = n / 1e6;
    const text = Math.abs(m) >= 0.05 ? decimal(m, 1) + 'm' : `${m < 0 ? '-' : ''}<0.1m`;
    return { text, title, isZero: false };
};

// The variance badge that sits beside a Budget Overview figure. Not money — one
// decimal, ungrouped, no unit suffix — but a negative takes the same parentheses as
// the figure next to it. Returns '' for anything non-numeric.
export const formatOverviewVariance = (value) => {
    const n = Number(value);

    if (!isFinite(n)) {
        return '';
    }

    const rounded = Number(n.toFixed(1));

    return bracketNegative(rounded, formatterFor(locale(), 0, 1, false).format(Math.abs(rounded)));
};

// Quantities are products of float slot amounts, so they can carry tiny
// floating-point artefacts (e.g. 12.0000001). Round to a sane precision and
// drop trailing zeros so whole quantities render as "12", not "12.0000".
export const formatQuantity = (val, precision = 4) => {
    const numeric = Number(val);
    if (!isFinite(numeric)) {
        return 0;
    }

    return Number(numeric.toFixed(precision));
};

/**
 * Format a quantity for display in the currency's locale — grouped like money,
 * at most one decimal place — and blank for null/empty/non-numeric input. The
 * single quantity format for every screen that shows a Qty beside money.
 */
export const formatQtyDisplay = (value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return '';
    return decimalUpTo(num, 1);
};

export const formatCommittingQty = formatQtyDisplay;

export const formatPercentage = (p) => {
    if (p === null || p === undefined || isNaN(p)) {
        return 0;
    }

    const numeric = parseFloat(p);
    if (numeric < 0) {
        return 0;
    }

    return parseFloat(numeric.toFixed(2));
};

export const truncateDecimals = (value, precision = 2) => {
    if (value === null || value === undefined || isNaN(value)) {
        return 0;
    }

    const numeric = Number(value);
    if (!isFinite(numeric)) {
        return 0;
    }

    const factor = Math.pow(10, precision);
    return Math.trunc(numeric * factor) / factor;
};

export const toFullPrecisionString = (value) => {
    if (value === null || value === undefined || value === '') {
        return '0';
    }

    const numeric = Number(value);
    if (!isFinite(numeric)) {
        return '0';
    }

    return numeric.toString();
};

// Swaps the locale's own group separator for the caller's, so the substitution
// still lands under a currency that doesn't group with commas.
export const formatToFixedSeparateThousands = (val, separator = ' ') => {
    val = Math.abs(Math.round(val));
    const grouped = formatSeparateByThousands(Number(val));
    const { group } = separators();

    return group ? grouped.split(group).join(separator) : grouped;
};

export const formatPrice = (number, decimals = 0) => {
    if (number !== undefined) {
        if (decimals === 'any') {
            let num = number.toString();

            if (num.length > 1) {
                num = num.replace(/^0+/, '');
            }

            const localeSeparators = separators();
            let parts = ((num || num === '0' ? number : '') + '').split('.');

            if (parts.length) {
                parts[0] = parts[0].replace(/(\d)(?=(\d{3})+\b)/g, '$1' + localeSeparators.group);
            }

            return parts.join(localeSeparators.decimal);
        } else {
            return decimal(Number(number).toFixed(decimals));
        }
    }
};

// Called once on page load, against the value Blade rendered into the field —
// always a plain PHP number with a dot decimal, never locale-formatted. It is
// therefore read with Number() rather than the locale parser: under a currency
// that groups with dots, parsing "14250000.5" by locale rules would read it as
// 142500005.
export const thousandsFormat = ($this, $decimals = 0) => {
    let number = Number($this.val());
    if (number !== undefined) {
        let numberFormatted = formatPrice(number, $decimals);
        $this.val(numberFormatted);
    }
};

// The characters a figure may group with. Space-grouping locales accept every
// space variant and apostrophe-grouping ones both apostrophes, as unformat()
// does.
const groupCharacters = (group) => {
    if (/\s/.test(group)) {
        return [' ', '\u00a0', '\u202f'];
    }

    return ["'", '\u2019'].includes(group) ? ["'", '\u2019'] : [group].filter(Boolean);
};

const stripCharacters = (text, characters) => characters.reduce((stripped, character) => stripped.split(character).join(''), text);

const fractionDigits = (canonicalText) => (canonicalText.split('.')[1] || '').length;

/**
 * Read a figure this application formatted itself — a live money field, or a
 * .thousands-format field after its load-time formatting — as a number, or
 * NaN. Every group character is stripped wherever it sits, so lakh grouping
 * ("12,34,567.00") reads correctly where unformat() only accepts groups of
 * three. Not for pasted text, where a stray separator must not be read as
 * grouping.
 */
export const parseFormattedMoney = (text, tag = locale()) => {
    if (typeof text === 'number') {
        return Number.isFinite(text) ? text : NaN;
    }

    const canonicalText = canonicalFormattedMoney(text, tag);

    return canonicalText === null ? NaN : Number(canonicalText);
};

/**
 * The same reading as parseFormattedMoney(), returned as a plain numeric STRING
 * ("1234567.0035") rather than a number, or null. A figure submitted from it keeps
 * every digit typed: going through a Number and back out through a formatter
 * rounds (canonical() keeps three decimals).
 */
export const canonicalFormattedMoney = (text, tag = locale()) => {
    if (text === null || text === undefined) {
        return null;
    }

    const { group, decimal: decimalSeparator } = separators(tag);
    let normalised = stripCharacters(String(text).trim(), groupCharacters(group)).replace(/^\u2212/, '-');

    if (decimalSeparator !== '.') {
        normalised = normalised.split(decimalSeparator).join('.');
    }

    return /^-?(\d+(\.\d*)?|\.\d+)$/.test(normalised) ? normalised : null;
};

// A pasted figure as a number, or NaN. Read in the currency's convention first;
// when that fails, or reads more decimals than the field takes, the opposite
// convention is tried: with both separators present the LAST one divides, and
// a lone separator kind only counts as grouping when every group after the
// first has exactly three digits. Anything still ambiguous is rejected.
export const parsePastedMoney = (text, decimals) => {
    const canonicalText = unformat(text);

    if (canonicalText !== null && !canonicalText.startsWith('-') && fractionDigits(canonicalText) <= decimals) {
        return Number(canonicalText);
    }

    const compactText = text.replace(/[\s'\u2019]/g, '');
    let candidate = null;

    if (/^\d[\d.,]*$/.test(compactText)) {
        const last = Math.max(compactText.lastIndexOf('.'), compactText.lastIndexOf(','));

        if (compactText.includes('.') && compactText.includes(',')) {
            const groupSeparator = compactText[last] === '.' ? ',' : '.';
            candidate = `${compactText.slice(0, last).split(groupSeparator).join('')}.${compactText.slice(last + 1)}`;
        } else if (last === -1) {
            candidate = compactText;
        } else if (/^\d{1,3}([.,]\d{3})+$/.test(compactText)) {
            candidate = compactText.replace(/[.,]/g, '');
        }
    }

    return candidate !== null && /^\d+(\.\d+)?$/.test(candidate) && fractionDigits(candidate) <= decimals ? Number(candidate) : NaN;
};

/**
 * Format a money input in the currency's convention AS the user types, and
 * report its value on every edit, so a figure derived from it can update live.
 *
 * Thousands separators appear while typing; the decimals are padded on blur.
 * A typed group separator is accepted and regrouped, and the decimal key is
 * limited to one separator and `decimals` digits, judged on the text the key
 * would actually produce at the caret. A "-" can't be typed, but a negative
 * the field already holds keeps its sign. Paste and drop follow
 * parsePastedMoney() and leave the field untouched when the text isn't a
 * figure. Read-only and disabled fields are left alone.
 *
 * The input must carry `data-live-money`, which keeps the generic
 * `.thousands-format` keystroke handlers off it. `onValue` receives a number,
 * 0 for an empty field.
 */
export const bindLiveMoneyInput = (input, { decimals = 2, onValue = () => {} } = {}) => {
    if (!input) {
        return;
    }

    const quote = (character) => character.replace(/[^\w]/g, (c) => '\\' + c);
    const isLocked = () => input.readOnly || input.disabled;
    const isSign = (character) => character === '-' || character === '\u2212';

    const valueOf = (text) => {
        const parsed = parseFormattedMoney(text);

        // `|| 0` turns -0 into 0.
        return (Number.isFinite(parsed) && parsed) || 0;
    };

    const isTypeable = (text) => {
        const { group, decimal: decimalSeparator } = separators();
        const digits = stripCharacters(text, groupCharacters(group));
        const fraction = decimals > 0 ? `(${quote(decimalSeparator)}\\d{0,${decimals}})?` : '';

        return new RegExp(`^[-\\u2212]?\\d*${fraction}$`).test(digits);
    };

    // Regroup the integer part and keep the decimal part exactly as typed — a
    // trailing separator means the user is mid-entry. The caret is restored by
    // counting the sign, digits and decimal separator to its left.
    const reformat = () => {
        const { decimal: decimalSeparator } = separators();
        const raw = input.value;
        const caret = input.selectionStart ?? raw.length;
        const sign = isSign(raw.charAt(0)) ? raw.charAt(0) : '';
        let kept = '';
        let keptBeforeCaret = sign && caret > 0 ? 1 : 0;

        for (let index = sign.length; index < raw.length; index++) {
            const character = raw[index];
            const isDigit = /\d/.test(character);
            const isFirstDecimal = decimals > 0 && character === decimalSeparator && !kept.includes(decimalSeparator);

            if (isDigit || isFirstDecimal) {
                kept += character;
                keptBeforeCaret += index < caret ? 1 : 0;
            }
        }

        let [integerPart, fractionPart] = kept.split(decimalSeparator);
        const leadingZeros = integerPart.length - integerPart.replace(/^0+(?=\d)/, '').length;
        integerPart = integerPart.slice(leadingZeros);
        keptBeforeCaret = Math.max(sign && caret > 0 ? 1 : 0, keptBeforeCaret - leadingZeros);

        const grouped = integerPart === '' ? '' : decimal(Number(integerPart), 0);
        // The sign stays while the figure is edited through zero ("-", "-0"),
        // so the next digit is still negative; blur drops it from a zero.
        const formatted = sign + (fractionPart === undefined ? grouped : `${grouped}${decimalSeparator}${fractionPart.slice(0, decimals)}`);

        let position = 0;
        for (let counted = 0; position < formatted.length && counted < keptBeforeCaret; position++) {
            if (/\d/.test(formatted[position]) || formatted[position] === decimalSeparator || (position === 0 && sign)) {
                counted++;
            }
        }

        input.value = formatted;

        if (document.activeElement === input) {
            input.setSelectionRange(position, position);
        }

        onValue(valueOf(formatted));
    };

    // A pasted or dropped figure replaces the whole value.
    const replaceWith = (text) => {
        const pasted = parsePastedMoney(text.trim(), decimals);

        if (!Number.isFinite(pasted)) {
            return;
        }

        input.value = decimalUpTo(pasted, decimals);
        onValue(pasted);
    };

    // Deleting only a group separator would be undone by the regroup, so the
    // nearest digit beyond it is deleted instead.
    const deleteAcrossGroup = (event) => {
        const { decimal: decimalSeparator } = separators();
        const value = input.value;
        const start = input.selectionStart;
        const forward = event.inputType === 'deleteContentForward';
        const step = forward ? 1 : -1;
        const isDeletable = (character) => /\d/.test(character) || character === decimalSeparator;
        let index = forward ? start : start - 1;

        if (start !== input.selectionEnd || index < 0 || index >= value.length || isDeletable(value[index]) || isSign(value[index])) {
            return;
        }

        while (index >= 0 && index < value.length && !isDeletable(value[index])) {
            index += step;
        }

        event.preventDefault();

        if (index < 0 || index >= value.length) {
            return;
        }

        input.value = value.slice(0, index) + value.slice(index + 1);
        input.setSelectionRange(forward ? start : index, forward ? start : index);
        reformat();
    };

    input.addEventListener('beforeinput', (event) => {
        if (isLocked() || event.isComposing || event.inputType === 'insertCompositionText') {
            return;
        }

        if (event.inputType === 'deleteContentForward' || event.inputType === 'deleteContentBackward') {
            deleteAcrossGroup(event);

            return;
        }

        if (!event.inputType.startsWith('insert') || event.inputType === 'insertFromPaste' || event.inputType === 'insertFromDrop' || !event.data) {
            return;
        }

        const value = input.value;
        const start = input.selectionStart ?? value.length;
        const end = input.selectionEnd ?? value.length;

        if (/[-\u2212]/.test(event.data) || !isTypeable(value.slice(0, start) + event.data + value.slice(end))) {
            event.preventDefault();
        }
    });

    // An IME rewrites the value on every keystroke of a composition, so the
    // field is only reformatted once the composition ends.
    input.addEventListener('input', (event) => {
        if (!isLocked() && !event.isComposing) {
            reformat();
        }
    });

    input.addEventListener('compositionend', () => {
        if (!isLocked()) {
            reformat();
        }
    });

    input.addEventListener('paste', (event) => {
        if (isLocked()) {
            return;
        }

        event.preventDefault();
        replaceWith(event.clipboardData?.getData('text') ?? '');
    });

    input.addEventListener('drop', (event) => {
        if (isLocked()) {
            return;
        }

        event.preventDefault();
        input.focus();
        replaceWith(event.dataTransfer?.getData('text') ?? '');
    });

    input.addEventListener('blur', () => {
        if (isLocked()) {
            return;
        }

        const value = valueOf(input.value);

        input.value = unsignZero(decimal(value, decimals));
        onValue(value);
    });
};

export const addTotal = (selector, total) => {
    if (selector instanceof jQuery) {
        $element = selector;
    } else {
        var $element = $(selector);
    }
    if ($element.length) {
        // Reads its own rendered output, adds, and writes it back — so the text
        // must be parsed with the currency's separators. Comma-stripping it
        // would compound a wrong magnitude on every call.
        var parsed = parseLocaleNumber($element.text());
        var newVal = Number.isFinite(parsed) ? parsed : 0;
        newVal = newVal + total;
        $element.text(formatPrice(newVal));
        if (typeof $element.data('add-parent') !== 'undefined') {
            addTotal('#' + $element.data('add-parent'), total);
        }
    }
};

document.addEventListener('DOMContentLoaded', function () {
    if ($('.thousands-format').length) {
        $('.thousands-format').each(function () {
            let $input = $(this);
            let $decimals = $input.data('decimals');
            thousandsFormat($input, $decimals !== undefined ? $decimals : '');

            // Formatted as they are typed by bindLiveMoneyInput().
            if (this.hasAttribute('data-live-money')) {
                return;
            }

            // The field shows a formatted value once the user starts typing, so
            // these read it with the currency's separators. Under TRY the decimal
            // key is "," and "." groups — the reverse of Anglo — so both the
            // allowed-key check and the decimal counting are driven off the
            // locale rather than hardcoded to ".".
            $input.on('keypress', function (event) {
                if (event.ctrlKey || event.metaKey) {
                    return;
                }

                const { group, decimal: decimalSeparator } = separators();
                const typed = $input.val().split(group).join('');
                const asNumber = decimalSeparator === '.' ? typed : typed.split(decimalSeparator).join('.');
                const decimalKeyCode = decimalSeparator.charCodeAt(0);
                const typedKey = event.key === decimalSeparator ? '.' : event.key;

                // Digits only. Testing Number() coercion instead reads every
                // whitespace character as 0, so a typed space reached the field
                // and made unformat fail — the value then submitted as 0.
                // Keys below 32 (Backspace, Enter, Tab) insert nothing, so they
                // keep their default action rather than being cancelled here.
                if (event.which >= 32 && !/^\d$/.test(String.fromCharCode(event.which))) {
                    if (!($decimals !== undefined && event.which === decimalKeyCode && $input.val().split(decimalSeparator).length - 1 < 1)) {
                        event.preventDefault();
                    }
                } else if ($decimals !== undefined && Number(asNumber + typedKey).countDecimals() > $decimals) {
                    event.preventDefault();
                }
            });
            $input.on('keyup', function (event) {
                if (event.ctrlKey || event.metaKey) {
                    return;
                }

                const { group, decimal: decimalSeparator } = separators();
                // Grouping removed, but the decimal separator left exactly as
                // typed: a trailing separator means the user is mid-entry, and
                // re-formatting then would delete it.
                const newVal = $input.val().split(group).join('');
                const asNumber = decimalSeparator === '.' ? newVal : newVal.split(decimalSeparator).join('.');

                /* skip format if the value is like X. => allow to add decimals */
                if ((!newVal.endsWith(decimalSeparator) && !newVal.endsWith('0')) || (newVal.endsWith('0') && newVal.split(decimalSeparator).length === 1)) {
                    $input.val(formatPrice(asNumber, $decimals !== undefined ? $decimals : ''));
                } else if (newVal.split(decimalSeparator).length - 1 > 1) {
                    $input.val(formatPrice(asNumber, $decimals !== undefined ? $decimals : ''));
                }
                if (isNaN(asNumber)) {
                    $input.val('');
                }
            });
        });

        /* Check all forms if it has numbers formatted with comma -> remove it */
        $('form').submit(function (event) {
            event.preventDefault(); //prevent the default submit
            // Replace all commas
            removeCommaFromNumbers();
            $(this).unbind('submit').submit(); // continue the submit unbind preventDefault
        });
    }

    Number.prototype.countDecimals = function () {
        // Callers reach this with Number(value + keyName) for control keys, so
        // NaN arrives on every Backspace and Enter. Without this the split on
        // "NaN" indexes undefined and throws, killing the rest of the handler.
        if (!Number.isFinite(this.valueOf())) return 0;
        if (Math.floor(this.valueOf()) === this.valueOf()) return 0;
        return this.toString().split('.')[1].length || 0;
    };
});

document.addEventListener('DOMContentLoaded', function () {
    if ($('.thousands-format-text').length) {
        $('.thousands-format-text').each(function () {
            let $input = $(this);
            let number = Number($input.text());
            if (number !== undefined) {
                let numberformated = formatPrice(number, 0);
                $input.text(numberformated);
            }
        });
    }
});

export const truncateString = (str, maxLength = 20) => {
    if (str.length > maxLength) {
        return str.substring(0, maxLength) + '...';
    }
    return str;
};

// Middle elision for identifiers whose ends carry the meaning — a document
// reference or hash is recognised by its prefix and suffix, so end-truncation
// (truncateString, above) throws away the half that distinguishes it. At the
// default maxLength of 16:
//   'INV-2026-0001-ACME-7741' -> 'INV-2026…ME-7741'
// The head takes the odd character (8 + ellipsis + 7 = 16). Strings already
// within maxLength are returned untouched.
export const elideMiddle = (str, maxLength = 16, ellipsis = '…') => {
    const value = str === null || str === undefined ? '' : String(str);

    if (value.length <= maxLength) {
        return value;
    }

    const keep = Math.max(maxLength - ellipsis.length, 2);
    const head = Math.ceil(keep / 2);
    const tail = keep - head;

    return value.slice(0, head) + ellipsis + (tail > 0 ? value.slice(value.length - tail) : '');
};

// Normalise formatted number inputs to a plain numeric value before submit, so
// the server receives "14250000.50" whatever convention the field displayed.
// Named for the Anglo case it originally handled; it now reads the currency's
// own separators, because comma-stripping "14.250.000,50" submits 14.25.
// Input that is not a number is left exactly as typed, so validation still sees
// what the user entered rather than a silently altered value.
export const removeCommaFromNumbers = () => {
    if ($('.thousands-format, .remove-comma').length > 0) {
        $('.thousands-format, .remove-comma').each(function () {
            const normalised = unformat($(this).val());
            if (normalised !== null) {
                $(this).val(normalised);
            }
        });
    }
};
